"use client";

import { revealFlowInput } from "./flow-focus";
import { useEffect, useLayoutEffect, useState } from "react";

/** Keep fixed form actions above an on-screen keyboard without reacting to pinch zoom. */
export function useFlowKeyboardInset(enabled: boolean) {
  const [inset, setInset] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const viewport = window.visualViewport;
    if (!viewport) return;
    let frame = 0;
    let keyboardOpen = false;
    const update = () => {
      const control = document.activeElement;
      const editing =
        control instanceof HTMLElement &&
        control.matches("input, textarea, [contenteditable=true]");
      const gap = Math.max(
        0,
        window.innerHeight - viewport.height - viewport.offsetTop,
      );
      if (viewport.scale !== 1 || gap === 0) keyboardOpen = false;
      else if (editing) keyboardOpen = true;
      // Keep the bar still when focus moves to Continue before the keyboard closes.
      const next = keyboardOpen ? gap : 0;
      setInset(next);
      cancelAnimationFrame(frame);
      if (next > 0) frame = requestAnimationFrame(revealFlowInput);
    };
    update();
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    document.addEventListener("focusin", update);
    document.addEventListener("focusout", update);
    return () => {
      cancelAnimationFrame(frame);
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      document.removeEventListener("focusin", update);
      document.removeEventListener("focusout", update);
    };
  }, [enabled]);
  useLayoutEffect(() => {
    if (enabled && inset > 0) revealFlowInput();
  }, [enabled, inset]);
  return inset;
}
